import React, { useEffect, useRef, useState } from "react";
import { useAuthContext } from "../Contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Logo from "../assets/Logo.svg";
import venezuelaimg from "../assets/VENEZUELA.jpg";
import europeimg from "../assets/Europe.jpg";
import BankSlider from "./BankSlider";

const API = process.env.REACT_APP_BASE_URL;

const MIN_AMOUNT = 5;
const MAX_AMOUNT = 3000;

/** "1.234,56" -> 1234.56 */
const parseEs = (v) =>
  typeof v === "number"
    ? v
    : parseFloat(String(v || "0").replaceAll(".", "").replace(",", ".")) || 0;

/** 1234.56 -> "1.234,56" */
const fmtEs = (n, d = 2) =>
  Number(n || 0).toLocaleString("es-ES", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

const Calculator = ({ btn }) => {
  const { user, userdata } = useAuthContext();
  const navigate = useNavigate();

  // ---- tabs ------------------------------------------------------------
  const [first, setFirst] = useState(true);

  // ---- corridor --------------------------------------------------------
  // Optimistic: the calculator is live from the first render. If PayTKS has no
  // EUR->VES corridor, the /quote response flips corridorReady to false below
  // and the "Servicio Temporalmente Pausado" screen appears on its own.
  const [booting] = useState(false);
  const [corridorReady, setCorridorReady] = useState(true);

  // ---- quote -----------------------------------------------------------
  const [amount, setAmount] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const quoteTimer = useRef(null);

  // ---- localizador -----------------------------------------------------
  const [status, setStatus] = useState("");
  const [id, setId] = useState("");

  // ---- notify me -------------------------------------------------------
  const [notifyEmail, setNotifyEmail] = useState(userdata?.email || "");
  const [notifySent, setNotifySent] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);

  /* ---------------------------------------------------------------- *
   * Debounced live quote
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (!corridorReady) return;

    const value = parseEs(amount);
    if (!value || value < MIN_AMOUNT) {
      setQuote(null);
      return;
    }

    clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(async () => {
      try {
        setQuoting(true);
        const { data } = await axios.get(
          `${API}/api/pmi/quote?amount=${value}&sentType=BANK`
        );
        if (data?.corridorReady === false) {
          setCorridorReady(false);
          setQuote(null);
        } else {
          setQuote(data);
        }
      } catch (err) {
        console.error(err);
        setQuote(null);
      } finally {
        setQuoting(false);
      }
    }, 400);

    return () => clearTimeout(quoteTimer.current);
  }, [amount, corridorReady]);

  /* ---------------------------------------------------------------- *
   * Handlers
   * ---------------------------------------------------------------- */

  const handleAmount = (e) => {
    const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 7);
    setAmount(digits ? fmtEs(Number(digits), 0) : "");
  };

  const Continue = () => {
    const value = parseEs(amount);
    if (!quote || value < MIN_AMOUNT) {
      toast.error(`Envíos solo a partir de ${MIN_AMOUNT},00€`, {
        position: "top-center",
        theme: "dark",
      });
      return;
    }
    if (value > MAX_AMOUNT) {
      toast.error(`Máximo ${fmtEs(MAX_AMOUNT, 0)},00€ por envío`, {
        position: "top-center",
        theme: "dark",
      });
      return;
    }
    // Carry the amount through so /prealertar starts pre-filled
    const qs = `?amount=${value}`;
    navigate(user ? `/prealertar${qs}` : `/registro${qs}`);
  };

  const CheckStatus = async () => {
    try {
      const clean = id.trim().toUpperCase();
      // PMI orders use TVP#####, legacy orders use TV#####
      const { data } = await axios.put(`${API}/api/orders/checkstatus`, {
        orderId: clean.replace(/^TVP/, "").replace(/^TV/, ""),
        reference: clean,
      });
      setStatus(data?.status);
    } catch (err) {
      toast.error("Número de Orden no existe", {
        position: "top-center",
        theme: "dark",
      });
      console.log(err);
    }
  };

  const CheckColor = (elem) => {
    const s = String(elem || "").toUpperCase();
    if (["PENDIENTE", "ANULADA", "CANCELLED", "FAILED", "PENDING_PAYMENT"].includes(s))
      return "red";
    if (["COMPLETADA", "COMPLETED"].includes(s)) return "green";
    return "blue";
  };

  const handleNotifyMe = async () => {
    if (!notifyEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notifyEmail)) {
      toast.error("Ingresa un email válido", {
        position: "top-center",
        theme: "dark",
      });
      return;
    }
    try {
      setNotifyLoading(true);
      await axios.post(`${API}/api/orders/notify`, { email: notifyEmail });
      setNotifySent(true);
      toast.success("¡Te avisaremos en cuanto estemos disponibles!", {
        position: "top-center",
        theme: "dark",
      });
    } catch (err) {
      toast.error("Hubo un error, intenta de nuevo", {
        position: "top-center",
        theme: "dark",
      });
    } finally {
      setNotifyLoading(false);
    }
  };

  /* ---------------------------------------------------------------- *
   * Render
   * ---------------------------------------------------------------- */

  return (
    <div className="calculator wv-calc2">
      <style>{`
        div.wv-calc2 {
          /* --- brand accent (Walletven red). Change here if the exact hex differs --- */
          --accent: #F6394F;
          --accent-strong: #e22440;
          --accent-tint: #fff1f3;
          --ink: #101828;
          --muted: #667085;
          --label: #475467;
          --field: #f5f6f8;
          --field-border: #ececf0;
          --card-border: #ececec;

          background: #ffffff;
          border: 1px solid var(--card-border);
          border-radius: 20px;
          padding: 24px;
          box-shadow: 0 10px 34px rgba(16, 24, 40, 0.05);
          color: var(--ink);
          box-sizing: border-box;
        }
        .wv-calc2 * { box-sizing: border-box; }

        .wv-calc2 .wv-logo { display: block; margin: 0 auto 18px; }

        /* segmented tabs */
        .wv-calc2 .wv-tabs {
          display: flex;
          background: var(--field);
          border-radius: 12px;
          padding: 4px;
          margin-bottom: 18px;
        }
        .wv-calc2 .wv-tab {
          flex: 1;
          border: none;
          background: none;
          padding: 10px;
          border-radius: 9px;
          font-weight: 600;
          font-size: 0.95rem;
          color: var(--muted);
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
        }
        .wv-calc2 .wv-tab.active {
          background: #ffffff;
          color: var(--accent);
          box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12);
        }

        .wv-calc2 .wv-tab.active:hover{
        color: red !important;
        }
        .wv-calc2 .wv-field { margin-top: 16px; }
        .wv-calc2 .wv-field:first-of-type { margin-top: 0; }
        .wv-calc2 .wv-label {
          display: block;
          font-weight: 600;
          font-size: 0.9rem;
          color: var(--label);
          margin-bottom: 8px;
        }

        /* combined input + flag + currency */
        .wv-calc2 .wv-field-box {
          display: flex;
          align-items: center;
          background: var(--field);
          border: 1px solid var(--field-border);
          border-radius: 11px;
          overflow: hidden;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }
        .wv-calc2 .wv-field-box:focus-within {
          background: #ffffff;
          border-color: var(--accent);
          box-shadow: 0 0 0 3px rgba(246, 57, 79, 0.14);
        }
        .wv-calc2 .wv-field-box input {
          flex: 1;
          min-width: 0;
          border: none;
          background: transparent;
          padding: 14px 16px;
          font-size: 1rem;
          color: var(--ink);
          outline: none;
        }
        .wv-calc2 .wv-field-box input:disabled { color: var(--ink); -webkit-text-fill-color: var(--ink); opacity: 1; }
        .wv-calc2 .wv-field-box .wv-flag {
          width: 38px;
          height: 26px;
          object-fit: cover;
          border-radius: 4px;
          margin-right: 6px;
        }
        .wv-calc2 .wv-field-box .wv-curr {
          align-self: stretch;
          display: flex;
          align-items: center;
          padding: 0 14px;
          font-weight: 700;
          font-size: 0.82rem;
          color: var(--muted);
          border-left: 1px solid var(--field-border);
          background: rgba(16, 24, 40, 0.02);
        }

        /* standalone input (email / localizador) */
        .wv-calc2 .wv-input {
          width: 100%;
          background: var(--field);
          border: 1px solid var(--field-border);
          border-radius: 11px;
          padding: 14px 16px;
          font-size: 1rem;
          color: var(--ink);
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }
        .wv-calc2 .wv-input::placeholder { color: #98a2b3; }
        .wv-calc2 .wv-input:focus {
          background: #ffffff;
          border-color: var(--accent);
          box-shadow: 0 0 0 3px rgba(246, 57, 79, 0.14);
        }

        /* summary */
        .wv-calc2 .wv-summary {
          margin-top: 16px;
          background: var(--field);
          border-radius: 12px;
          padding: 12px 16px;
        }
        .wv-calc2 .wv-summary .wv-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.9rem;
          color: var(--muted);
        }
        .wv-calc2 .wv-summary .wv-row + .wv-row { margin-top: 8px; }
        .wv-calc2 .wv-summary .wv-row b { color: var(--ink); font-weight: 700; }

        .wv-calc2 .wv-note { margin: 10px 0 0; font-size: 0.78rem; color: #98a2b3; }

        .wv-calc2 .wv-submit {
          width: 100%;
          margin-top: 18px;
          padding: 15px;
          background: var(--accent);
          color: #ffffff;
          font-weight: 700;
          font-size: 1rem;
          border: none;
          border-radius: 11px;
          cursor: pointer;
          transition: background 0.15s ease, transform 0.05s ease;
        }
        .wv-calc2 .wv-submit:hover:not(:disabled) { background: var(--accent-strong); }
        .wv-calc2 .wv-submit:active:not(:disabled) { transform: translateY(1px); }
        .wv-calc2 .wv-submit:disabled { opacity: 0.6; cursor: not-allowed; }

        /* paused state */
        .wv-calc2 .wv-paused { text-align: center; padding: 14px 4px; }
        .wv-calc2 .wv-paused-title { font-size: 1.05rem; font-weight: 700; color: var(--ink); margin: 0 0 8px; }
        .wv-calc2 .wv-paused-text { color: var(--muted); font-size: 0.92rem; line-height: 1.6; margin: 0 0 16px; }
        .wv-calc2 .wv-success { color: #12b76a; font-weight: 600; margin: 0; }

        /* status badge */
        .wv-calc2 .wv-status {
          display: inline-block;
          padding: 8px 22px;
          border-radius: 999px;
          font-weight: 700;
          font-size: 1rem;
          margin-bottom: 4px;
        }
        .wv-calc2 .wv-status.red { background: var(--accent-tint); color: var(--accent); }
        .wv-calc2 .wv-status.green { background: #e7f8f0; color: #12b76a; }
        .wv-calc2 .wv-status.blue { background: #eaf4ff; color: #2f8fe6; }

        /* skeleton while corridor health is loading */
        .wv-calc2 .wv-skeleton {
          height: 52px;
          border-radius: 11px;
          background: linear-gradient(90deg, var(--field) 25%, #eceef1 37%, var(--field) 63%);
          background-size: 400% 100%;
          animation: wv-shimmer 1.3s ease infinite;
        }
        .wv-calc2 .wv-skeleton + .wv-skeleton { margin-top: 16px; }
        @keyframes wv-shimmer {
          0% { background-position: 100% 50%; }
          100% { background-position: 0 50%; }
        }
      `}</style>

      <img
        src={Logo}
        height={window.matchMedia("(max-width: 500px)").matches ? 40 : 50}
        className="wv-logo"
        alt="Transferven"
      />

      <div className="wv-tabs">
        <button
          type="button"
          className={`wv-tab ${first ? "active" : ""}`}
          onClick={() => setFirst(true)}
        >
          Calcular
        </button>
        <button
          type="button"
          className={`wv-tab ${!first ? "active" : ""}`}
          onClick={() => setFirst(false)}
        >
          Localizador
        </button>
      </div>

      {first ? (
        booting ? (
          <>
            <div className="wv-skeleton" />
            <div className="wv-skeleton" />
            <div className="wv-skeleton" />
          </>
        ) : !corridorReady ? (
          <div className="wv-paused">
            <h5 className="wv-paused-title">Servicio Temporalmente Pausado</h5>
            <p className="wv-paused-text">
              Los servicios Transferven se están adecuando a las nuevas normativas
              legales. Estaremos disponibles en breve. Disculpen las molestias.
            </p>
            {!notifySent ? (
              <div className="mt-3">
                {!userdata?.email && (
                  <input
                    type="email"
                    className="wv-input"
                    style={{ marginBottom: "12px" }}
                    placeholder="Tu correo electrónico"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                  />
                )}
                <button
                  type="button"
                  className="wv-submit"
                  style={{ marginTop: 0 }}
                  onClick={handleNotifyMe}
                  disabled={notifyLoading}
                >
                  {notifyLoading ? "Enviando..." : "Avísame cuando esté disponible"}
                </button>
              </div>
            ) : (
              <p className="wv-success">
                ✓ Te avisaremos por correo en cuanto reactivemos el servicio.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="wv-field">
              <label className="wv-label">Envía</label>
              <div className="wv-field-box">
                <input
                  type="text"
                  inputMode="numeric"
                  value={amount}
                  onChange={handleAmount}
                  placeholder="0"
                />
                <img className="wv-flag" src={europeimg} alt="" />
                <div className="wv-curr">EUR</div>
              </div>
            </div>

            <div className="wv-field">
              <label className="wv-label">Recibe*</label>
              <div className="wv-field-box">
                <input
                  type="text"
                  value={quoting ? "..." : quote ? fmtEs(quote.receives) : ""}
                  disabled
                />
                <img className="wv-flag" src={venezuelaimg} alt="" />
                <div className="wv-curr">{quote?.currencyTo || "VES"}</div>
              </div>
            </div>

            <div className="wv-summary">
              <div className="wv-row">
                <span>Comisión</span>
                <b>{quote ? `${fmtEs(quote.fee)}€` : "—"}</b>
              </div>
              <div className="wv-row">
                <span>Tasa</span>
                <b>
                  1 EUR ={" "}
                  {quote
                    ? `${fmtEs(quote.fxRate, 4)} ${quote.currencyTo}`
                    : "—"}
                </b>
              </div>
            </div>

            <div className="wv-field">
              <label className="wv-label">Total a pagar</label>
              <div className="wv-field-box">
                <input
                  type="text"
                  value={quote ? fmtEs(quote.gross) : ""}
                  disabled
                />
                <div className="wv-curr">EUR</div>
              </div>
            </div>

            <p className="wv-note">
              *Resultado sujeto a tasas y tipos de cambio variables. Envíos desde{" "}
              {MIN_AMOUNT},00€ hasta {fmtEs(MAX_AMOUNT, 0)},00€.
            </p>

            {btn && (
              <button
                onClick={Continue}
                className="wv-submit"
                disabled={quoting || !quote}
              >
                Continuar
              </button>
            )}
          </>
        )
      ) : (
        <div>
          {status?.length === 0 ? (
            <div className="wv-field">
              <label className="wv-label">Número de Órden</label>
              <input
                className="wv-input"
                value={id}
                onChange={(e) => setId(e.target.value)}
                placeholder="Ej. 50001"
                required
              />
              <button type="button" onClick={CheckStatus} className="wv-submit">
                BUSCAR
              </button>
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
              <span className={`wv-status ${CheckColor(status)}`}>
                {String(status).toUpperCase()}
              </span>
              <button onClick={() => setStatus("")} className="wv-submit">
                Volver
              </button>
            </div>
          )}
        </div>
      )}

      {/* {corridorReady && !booting && <BankSlider />} */}
    </div>
  );
};

export default Calculator;